/**
 * Production-file compose engine.
 *
 * All generation logic extracted from ProductionFileGenerator.vue so it can be
 * driven headlessly (per-row download, batch "Download All", nesting input).
 * The engine composes onto its own detached canvas and returns strings +
 * metadata — it never toasts, downloads, or emits.
 *
 * Differences vs the previously shipped component (intentional fixes):
 *  - Grading works again: the base size is measured before the target size
 *    (the component only ever loaded the target size, so every grading ratio
 *    collapsed to 1 and all sizes exported base-size geometry). This mirrors
 *    admin v1's `addSize('L', true)` + `addSize(size)` sequence.
 *  - Size groups are measured from the DOM only and are NOT rendered into the
 *    output. Admin v1 never added them to the canvas (its `canvas.add` was
 *    commented out); the v2 port added them, leaking a size-chart outline
 *    layer into shipped files.
 *  - `sizesUrl` is optional: without it the file is generated ungraded at
 *    base size and flagged with the `ungraded-base-size` warning.
 */
import {
  Canvas as FabricCanvas,
  Group as FabricGroup,
  Rect as FabricRect,
  Pattern as FabricPattern,
  Gradient as FabricGradient,
  FabricImage,
  FabricObject,
  Path as FabricPath,
  Point,
  loadSVGFromString,
  loadSVGFromURL,
  config as fabricConfig,
  util as fabricUtil,
} from 'fabric';
import { dedupeClipPathIds } from '@/utils/productionFile/svgSanitize';
import { isTextPlacementOn } from '@/utils/productionFile/textVisibility';
import { intersectPathData } from '@/utils/productionFile/offset';
import {
  IDENTITY,
  multiplyMat,
  parseTransform,
  shapeToPathData,
  type Mat,
} from '@/utils/productionFile/clipFlatten';
import * as opentypeNs from 'opentype.js';
import {
  descendSingleGroupWrappers,
  resolveBaseSizeGroupId,
  resolveSizeGroupId,
  sizeGroupIds,
} from '@/utils/productionFile/sizeIds';
import {
  arcSizeCompensation,
  effectiveArcMode,
  layoutArcText,
  wantsArcText,
  type ArcTextFields,
  type ArcVariant,
} from '@/utils/productionFile/arcText';

const opentypeLoad: (url: string, cb: (err: Error | null, font: any) => void) => void =
  (opentypeNs as any).load ?? (opentypeNs as any).default?.load;

type AnyObj = FabricObject & Record<string, any>;

export interface SvgGroupConfig {
  id: string;
  color?: string;
  gradient_colors?: { color: string }[];
}

export interface PatternConfig {
  pattern_url: string;
  random_string: string;
  color?: { value: string };
  scale: string | number;
  angle?: number;
  name?: string;
  url?: string;
}

export interface LogoConfig {
  url: string;
  left: number;
  top: number;
  scaleX?: number;
  scaleY?: number;
  angle?: number;
  is_customizable?: boolean;
  placement_title?: string;
}

export interface CustomLogoConfig {
  /** Storage key; entries without a url are skipped by the compose loop. */
  url?: string;
  x_axis: number;
  y_axis: number;
  x_axis_3d?: number;
  y_axis_3d?: number;
  height: number;
  rotation: number;
  scaleX?: number;
  scaleY?: number;
}

export interface TextItemConfig extends ArcTextFields {
  /** Placement switched on in the builder; `false`/0/'0' = removed by the customer. */
  selected?: boolean | number | string | null;
  x_axis: number;
  y_axis: number;
  x_axis_3d?: number;
  y_axis_3d?: number;
  height: number;
  rotation: number;
  color: string;
  outline_color: string;
  outline_width: string | number;
  scaleX?: number;
  scaleY?: number;
}

export interface CustomTextConfig {
  value: string;
  font_url: string;
  items: TextItemConfig[];
}

export interface ProductionComposeInput {
  designUrl: string;
  /** Sizes (grading) SVG. Absent/empty → ungraded base-size output. */
  sizesUrl?: string | null;
  safeZoneUrl?: string | null;
  /** Roster size to generate (must match a `<g id>` in the sizes SVG when grading). */
  size: string;
  canvasResolution?: number;
  containerWidth?: number;
  containerHeight?: number;
  twoDCanvasWidth?: number;
  twoDCanvasHeight?: number;
  groupPatterns?: Record<string, PatternConfig>;
  logos?: LogoConfig[];
  custom_logos?: CustomLogoConfig[];
  texts?: CustomTextConfig[];
  svgGroups?: SvgGroupConfig[];
  /** Override for tests; defaults to VITE_APP_FILE_URL. */
  storageUrl?: string;
}

export type PanelBoxSource = 'clipPath' | 'leafId' | 'topGroup';

/** Axis-aligned panel bounds in canvas space (pre-unflip, 0..canvasResolution). */
export interface PanelBox {
  name: string;
  left: number;
  top: number;
  width: number;
  height: number;
  source: PanelBoxSource;
  /**
   * Exact serialized id of the panel's element in the composite SVG (group or
   * leaf id). Lets the splitter carve out ONLY this panel's subtree — panel
   * bboxes overlap each other in real atlases, so window-cropping the whole
   * artwork would drag neighbor panels into every piece. Unset for
   * clipPath-derived panels (their owners aren't 1:1 elements).
   */
  sourceId?: string;
}

/** Canvas-space bounds of one name/number/logo object, in add order. */
export interface OverlayBox {
  type: 'logo' | 'text';
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface ComposeResult {
  svg: string;
  sizeFound: boolean;
  /** True when a base size was measured and per-panel ratios applied. */
  graded: boolean;
  /** Physical output dimensions (Anchor-derived, PostScript points). */
  widthPt: number;
  heightPt: number;
  /** The square canvas size the svg coordinates live in (viewBox side). */
  viewBoxSize: number;
  panels: PanelBox[];
  /** Logo/text objects sitting above the design, in add (= document) order. */
  overlays: OverlayBox[];
  warnings: string[];
  /** Storage keys of placed custom logos, in add order (for vector inlining). */
  customLogoKeys: string[];
}

export interface ProductionAssetCache {
  text: Map<string, Promise<string>>;
  font: Map<string, Promise<any>>;
  dataUrl: Map<string, Promise<string>>;
  /** Shared with logoVectorize: storage key -> raw svg text (null = known failure). */
  logoSvg: Map<string, string | null>;
}

export function createProductionAssetCache(): ProductionAssetCache {
  return { text: new Map(), font: new Map(), dataUrl: new Map(), logoSvg: new Map() };
}

export const PRODUCTION_BASE_SIZE = 'L';

/**
 * fabric 7 made center/center the default origin (fabric 6: left/top). The
 * engine's layout maths (inherited from admin v1) places groups by their
 * top-left edge, so every group it positions with `left/top` pins this.
 */
const TOP_LEFT_ORIGIN = { originX: 'left', originY: 'top' } as const;

/** Per-panel bounding boxes, keyed by the lowercased first id token ("frontpanel"). */
type PanelSpec = Record<string, { width: number; height: number }>;

/** What the production atlas itself says about the size it is drawn at. */
interface DesignAtlasInfo {
  /** Wrapper group id (Production > L > …) or the platform default. */
  baseToken: string;
  /** Cut-panel dimensions of the atlas (clipPath shapes), in artboard units. */
  panelDims: PanelSpec;
}

/**
 * containerWidth/twoDCanvasWidth is the builder's artwork sizing ratio: the 3D
 * texture paints logos and names at canonical scale × (main preview bitmap 760 /
 * main canvas reference 600) — the widget's ARTWORK_SIZING_RATIO, and the same
 * 760/600 that canonicalLogoPlacement.computeMeasurementK feeds into the reported
 * cm size. Production must reproduce that texture 1:1, so the ratio here is
 * 760/600, not the v1-era 512/600 (the container width of an old layout).
 */
export const COMPOSE_DEFAULTS = {
  canvasResolution: 2048,
  containerWidth: 760,
  containerHeight: 760,
  twoDCanvasWidth: 600,
  twoDCanvasHeight: 600,
} as const;

interface EngineState {
  canvas: FabricCanvas;
  design: FabricGroup;
  safeZone?: FabricGroup;
  mainSizeId: string;
  /** Factor the graded layout was spread by about the atlas centre (1 = none). */
  gradingSpread: number;
  sizeSpec: Record<string, { width: number; height: number }>;
  mainSizeSpec: Record<string, { width: number; height: number }>;
  sizeDifferencesRatio: Record<string, { width: number; height: number }>;
  patterns: Record<string, FabricPattern | FabricRect>;
  warnings: string[];
  customLogoKeys: string[];
}

function shallowClone<T>(obj: T): T {
  return Object.assign(Object.create(Object.getPrototypeOf(obj as any)), obj) as T;
}

function cloneFabricObjects(objects: FabricObject[]): FabricObject[] {
  return objects.map((o) => shallowClone(o));
}

interface CanvasBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Canvas-space box via corner points through calcTransformMatrix() —
 *  getBoundingRect() goes stale for group children after later rescales. */
function canvasBox(obj: AnyObj): CanvasBox {
  return matrixCanvasBox(
    (obj.width ?? 0) as number,
    (obj.height ?? 0) as number,
    obj.calcTransformMatrix()
  );
}

/** Axis-aligned canvas box of a w×h object centred under `matrix`. */
function matrixCanvasBox(w: number, h: number, matrix: number[]): CanvasBox {
  const corners = [
    { x: -w / 2, y: -h / 2 },
    { x: w / 2, y: -h / 2 },
    { x: w / 2, y: h / 2 },
    { x: -w / 2, y: h / 2 },
  ].map((p) => fabricUtil.transformPoint(p as any, matrix as never));
  const xs = corners.map((p) => p.x);
  const ys = corners.map((p) => p.y);
  const left = Math.min(...xs);
  const top = Math.min(...ys);
  return { left, top, width: Math.max(...xs) - left, height: Math.max(...ys) - top };
}

function unionBox(a: CanvasBox | null, b: CanvasBox | null): CanvasBox | null {
  if (!a) return b;
  if (!b) return a;
  const left = Math.min(a.left, b.left);
  const top = Math.min(a.top, b.top);
  return {
    left,
    top,
    width: Math.max(a.left + a.width, b.left + b.width) - left,
    height: Math.max(a.top + a.height, b.top + b.height) - top,
  };
}

function intersectBox(a: CanvasBox, b: CanvasBox): CanvasBox | null {
  const left = Math.max(a.left, b.left);
  const top = Math.max(a.top, b.top);
  const right = Math.min(a.left + a.width, b.left + b.width);
  const bottom = Math.min(a.top + a.height, b.top + b.height);
  return right > left && bottom > top
    ? { left, top, width: right - left, height: bottom - top }
    : null;
}

/** Canvas box of an object's clipPath (relative clips compose with the owner). */
function clipCanvasBox(owner: AnyObj, clip: AnyObj): CanvasBox {
  const clipMatrix = clip.calcTransformMatrix();
  const matrix = clip.absolutePositioned
    ? clipMatrix
    : fabricUtil.multiplyTransformMatrices(owner.calcTransformMatrix(), clipMatrix);
  return matrixCanvasBox((clip.width ?? 0) as number, (clip.height ?? 0) as number, matrix);
}

/**
 * The VISIBLE canvas box of an object: leaves clipped by their clipPath,
 * groups as the union of their children's visible boxes. Atlas panel groups
 * routinely hold a background shape spanning most of the artboard that is
 * clipped to the panel silhouette — their plain bbox says "whole atlas",
 * their visible box says "this panel".
 */
function visibleCanvasBox(obj: AnyObj): CanvasBox | null {
  const children = (obj._objects as AnyObj[] | undefined) ?? [];
  let box: CanvasBox | null = null;
  if (children.length) {
    for (const child of children) box = unionBox(box, visibleCanvasBox(child));
  } else {
    box = canvasBox(obj);
  }
  const clip = obj.clipPath as AnyObj | undefined;
  if (clip && box) box = intersectBox(box, clipCanvasBox(obj, clip)) ?? box;
  return box;
}

function intersectionArea(a: CanvasBox, b: CanvasBox): number {
  const w = Math.min(a.left + a.width, b.left + b.width) - Math.max(a.left, b.left);
  const h = Math.min(a.top + a.height, b.top + b.height) - Math.max(a.top, b.top);
  return w > 0 && h > 0 ? w * h : 0;
}

function oppositeAngle(angle: number): number {
  return (180 - angle + 360) % 360;
}

function roundOff(n: number): number {
  return Math.round(n * 1_000_000) / 1_000_000;
}

function svgToDataURL(svgElement: Element): string {
  const str = new XMLSerializer().serializeToString(svgElement);
  return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(str)))}`;
}

export interface ProductionFileEngine {
  compose: (input: ProductionComposeInput) => Promise<ComposeResult>;
  cache: ProductionAssetCache;
}

export function useProductionFileEngine(
  cache: ProductionAssetCache = createProductionAssetCache()
): ProductionFileEngine {
  function cachedText(url: string): Promise<string> {
    let hit = cache.text.get(url);
    if (!hit) {
      hit = fetch(url).then((r) => {
        if (!r.ok) throw new Error(`Failed to fetch ${url}: HTTP ${r.status}`);
        return r.text();
      });
      hit.catch(() => cache.text.delete(url));
      cache.text.set(url, hit);
    }
    return hit;
  }

  function cachedFont(url: string): Promise<any> {
    let hit = cache.font.get(url);
    if (!hit) {
      hit = new Promise((resolve) => {
        opentypeLoad(url, (err, font) => resolve(err ? null : font));
      });
      cache.font.set(url, hit);
    }
    return hit;
  }

  function cachedDataUrl(url: string): Promise<string> {
    let hit = cache.dataUrl.get(url);
    if (!hit) {
      hit = new Promise((resolve, reject) => {
        const img = Object.assign(new Image(), { crossOrigin: 'anonymous', src: url });
        img.onload = () => {
          const c = Object.assign(document.createElement('canvas'), {
            width: img.width,
            height: img.height,
          });
          c.getContext('2d')!.drawImage(img, 0, 0);
          resolve(c.toDataURL('image/png'));
        };
        img.onerror = reject;
      });
      hit.catch(() => cache.dataUrl.delete(url));
      cache.dataUrl.set(url, hit);
    }
    return hit;
  }

  async function compose(input: ProductionComposeInput): Promise<ComposeResult> {
    const canvasResolution = input.canvasResolution ?? COMPOSE_DEFAULTS.canvasResolution;
    const containerWidth = input.containerWidth ?? COMPOSE_DEFAULTS.containerWidth;
    const containerHeight = input.containerHeight ?? COMPOSE_DEFAULTS.containerHeight;
    const twoDCanvasWidth = input.twoDCanvasWidth ?? COMPOSE_DEFAULTS.twoDCanvasWidth;
    const twoDCanvasHeight = input.twoDCanvasHeight ?? COMPOSE_DEFAULTS.twoDCanvasHeight;
    const canvasWidthRatio = containerWidth / twoDCanvasWidth;
    const canvasHeightRatio = containerHeight / twoDCanvasHeight;
    const storageUrl = input.storageUrl ?? `${(import.meta as any).env?.VITE_APP_FILE_URL ?? ''}/`;
    const groupPatterns = input.groupPatterns ?? {};

    const canvasEl = document.createElement('canvas');
    canvasEl.width = canvasResolution;
    canvasEl.height = canvasResolution;

    const state: EngineState = {
      canvas: new FabricCanvas(canvasEl, { selection: false }),
      design: undefined as unknown as FabricGroup,
      safeZone: undefined,
      mainSizeId: '',
      gradingSpread: 1,
      sizeSpec: {},
      mainSizeSpec: {},
      sizeDifferencesRatio: {},
      patterns: {},
      warnings: [],
      customLogoKeys: [],
    };

    try {
      let graded = false;
      if (input.sizesUrl) {
        const atlas = await inspectDesignAtlas();
        const baseFound = await measureSizeSpec(atlas.baseToken, true, atlas);
        if (!baseFound) state.warnings.push('base-size-not-found');

        const sizeFound = await measureSizeSpec(input.size, false);
        if (!sizeFound) {
          return emptyResult(false, graded);
        }
        calculateSizeDifferences();
        graded = baseFound;
      } else {
        state.warnings.push('ungraded-base-size');
      }

      await addDesign();

      // Graded panels can extend past the Anchor square; grow the sheet (same
      // physical scale per unit) rather than crop them.
      const outputSize = fitCanvasToContent();
      const scaleUp = outputSize / canvasResolution;
      const base = resolveTargetDimensions();
      const widthPt = base.widthPt * scaleUp;
      const heightPt = base.heightPt * scaleUp;
      const panels = extractPanels();
      const overlays = extractOverlays();
      const svg = generateProductionSVG(widthPt, heightPt, outputSize);

      return {
        svg,
        sizeFound: true,
        graded,
        widthPt,
        heightPt,
        viewBoxSize: outputSize,
        panels,
        overlays,
        warnings: state.warnings,
        customLogoKeys: state.customLogoKeys,
      };
    } finally {
      await state.canvas.dispose();
    }

    function emptyResult(sizeFound: boolean, graded: boolean): ComposeResult {
      return {
        svg: '',
        sizeFound,
        graded,
        widthPt: 0,
        heightPt: 0,
        viewBoxSize: canvasResolution,
        panels: [],
        overlays: [],
        warnings: state.warnings,
        customLogoKeys: state.customLogoKeys,
      };
    }

    /**
     * Name/number/logo objects above the design, in add (= document) order.
     * Identified structurally (everything on the canvas except the design
     * group): fabric v6 ignores `set({ type: 'logo' })` — `type` is a readonly
     * getter — so the legacy type tags never stick.
     */
    function overlayObjects(): AnyObj[] {
      return state.canvas.getObjects().filter((o: AnyObj) => o !== (state.design as AnyObj));
    }

    function extractOverlays(): OverlayBox[] {
      return overlayObjects().map((o: AnyObj): OverlayBox => {
        const r = o.getBoundingRect();
        return {
          type: o instanceof FabricImage ? 'logo' : 'text',
          left: r.left,
          top: r.top,
          width: r.width,
          height: r.height,
        };
      });
    }

    /**
     * Measure per-panel bounding boxes of one size group in the sizes SVG.
     * DOM-only (getBBox needs an attached element); nothing is rendered.
     */
    async function measureSizeSpec(
      sizeParam: string,
      isMainSize: boolean,
      atlas?: DesignAtlasInfo
    ): Promise<boolean> {
      const svgText = await cachedText(storageUrl + input.sizesUrl);
      const svgDoc = new DOMParser().parseFromString(svgText, 'image/svg+xml');

      // An SVG id cannot hold a space, so design tools rewrite the layer name
      // on export ("Men XL" -> Illustrator's `Men_x20_XL`). Resolve the roster
      // label against the document's actual ids instead of comparing verbatim.
      const available = sizeGroupIds(svgDoc);
      // Anchor lookup needs the parsed document; do it before the root moves
      // into the measuring mount below.
      const anchored = isMainSize ? findMainSizeId(svgDoc) : null;

      const hiddenDiv = document.createElement('div');
      hiddenDiv.style.cssText = 'position:absolute;left:-9999px;visibility:hidden';
      hiddenDiv.appendChild(svgDoc.documentElement);
      document.body.appendChild(hiddenDiv);

      const specOf = (groupId: string): PanelSpec => {
        const spec: PanelSpec = {};
        const groupEl = hiddenDiv.querySelector(`g[id="${cssEscape(groupId)}"]`);
        groupEl?.childNodes.forEach((child) => {
          const el = child as Element & { id?: string; getBBox?: () => DOMRect };
          if (el.nodeType === Node.ELEMENT_NODE && el.id) {
            const id = el.id.split('_')[0].toLowerCase();
            const bbox = el.getBBox?.();
            if (bbox) spec[id] = { width: bbox.width, height: bbox.height };
          }
        });
        return spec;
      };

      try {
        let targetSizeId = sizeParam;
        if (isMainSize) {
          // Grading base, in order of trust: the size group holding the sizes
          // SVG's Anchor; the group whose cut panels have the same dimensions
          // as the atlas's clip panels — sizes layer and atlas share one
          // artboard, and the atlas (the 3D model's UV) is drawn at exactly
          // one size, which need NOT be L, so every group is scored; the
          // group named like the atlas wrapper / default "L" (family-aware);
          // and only then the first group in the file — which used to be
          // taken silently and graded Men 4XL DOWN off a Men_5XL listed first.
          const targetResolved = resolveSizeGroupId(available, input.size)?.id ?? null;
          const geometric = anchored
            ? null
            : pickBaseByGeometry(available, specOf, atlas?.panelDims);
          const inferred =
            anchored || geometric
              ? null
              : resolveBaseSizeGroupId(available, sizeParam, targetResolved);
          if (anchored) {
            targetSizeId = anchored;
          } else if (geometric) {
            targetSizeId = geometric;
            state.warnings.push(
              `base-size-inferred:${sizeParam}->${geometric} (matched the atlas panels)`
            );
          } else if (inferred) {
            targetSizeId = inferred.id;
            if (!inferred.exact)
              state.warnings.push(`base-size-inferred:${sizeParam}->${inferred.id}`);
          } else if (available[0]) {
            // Last resort: first group with a NON-EMPTY id (sizes SVGs often
            // carry an anonymous wrapper group with id="" as their first child).
            targetSizeId = available[0];
            state.warnings.push(`base-size-assumed-first-group:${available[0]}`);
          }
          state.mainSizeId = targetSizeId;
        } else {
          const resolved = resolveSizeGroupId(available, sizeParam);
          targetSizeId = resolved?.id ?? sizeParam;
          if (resolved && !resolved.exact) {
            const note = `size-id-matched-loosely:${sizeParam}->${resolved.id}`;
            if (!state.warnings.includes(note)) state.warnings.push(note);
          }
        }

        if (!targetSizeId || !hiddenDiv.querySelector(`g[id="${cssEscape(targetSizeId)}"]`)) {
          return false;
        }

        const spec = specOf(targetSizeId);
        if (isMainSize) state.mainSizeSpec = spec;
        else state.sizeSpec = spec;
        return true;
      } finally {
        hiddenDiv.parentNode?.removeChild(hiddenDiv);
      }
    }

    /**
     * The candidate whose panel dimensions match the atlas's clip panels: mean
     * |ln(candidate/atlas)| over the panels both know, accepted under a 5%
     * tolerance (graded neighbours differ by 4–8% per step, so the true base
     * scores ~0 and the next size never gets under the bar). Null when the
     * atlas gave no panel dims or nothing comes close.
     */
    function pickBaseByGeometry(
      candidateIds: string[],
      specOf: (groupId: string) => PanelSpec,
      panelDims?: PanelSpec
    ): string | null {
      if (!panelDims || !Object.keys(panelDims).length) return null;
      let best: { id: string; score: number } | null = null;
      for (const id of candidateIds) {
        const spec = specOf(id);
        const keys = Object.keys(spec).filter((k) => panelDims[k]);
        if (!keys.length) continue;
        const score =
          keys.reduce((sum, k) => {
            const w = Math.abs(Math.log(spec[k].width / panelDims[k].width));
            const h = Math.abs(Math.log(spec[k].height / panelDims[k].height));
            return sum + (w + h) / 2;
          }, 0) / keys.length;
        if (!best || score < best.score) best = { id, score };
      }
      return best && best.score < 0.05 ? best.id : null;
    }

    function findMainSizeId(svgDoc: Document): string | null {
      const anchor = svgDoc.getElementById('Anchor');
      if (!anchor) return null;
      let parent: Element | null = anchor.parentElement;
      while (parent && parent !== svgDoc.documentElement) {
        if (parent.tagName.toLowerCase() === 'g' && parent.getAttribute('id')) {
          return parent.getAttribute('id');
        }
        parent = parent.parentElement;
      }
      return null;
    }

    function calculateSizeDifferences(): void {
      const diff: EngineState['sizeDifferencesRatio'] = {};
      Object.keys(state.sizeSpec).forEach((key) => {
        const bw = state.mainSizeSpec[key]?.width ?? 0;
        const bh = state.mainSizeSpec[key]?.height ?? 0;
        const tw = state.sizeSpec[key]?.width ?? 0;
        const th = state.sizeSpec[key]?.height ?? 0;
        diff[key] = { width: bw ? tw / bw : 1, height: bh ? th / bh : 1 };
      });
      state.sizeDifferencesRatio = diff;
    }

    async function addDesign(): Promise<void> {
      const svgText = sanitizedDesignText(await cachedText(storageUrl + input.designUrl));

      const svgDoc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
      const hiddenDiv = document.createElement('div');
      hiddenDiv.style.cssText = 'position:absolute;left:-9999px;visibility:hidden';
      hiddenDiv.appendChild(svgDoc.documentElement);
      document.body.appendChild(hiddenDiv);

      try {
        // `elements[i]` is the source element of `objects[i]` (fabric keeps
        // them parallel; unsupported elements yield null objects). Grouping
        // walks up from THAT element, never from a getElementById lookup:
        // the uploader normalizes leaf ids to their first token, so a
        // mirrored master carries the same `Base`/`Stripe-1` ids in both
        // sleeves and in front AND back, and an id lookup files every
        // right-sleeve/back shape under the first match — one merged
        // "leftsleevepanel" piece with two sleeves in it, nesting broken.
        const { objects, elements } = await loadSVGFromString(svgText);
        const pairs = (objects ?? [])
          .map((obj, i) => ({
            obj: obj as AnyObj | null,
            el: elements?.[i] as Element | undefined,
          }))
          .filter((p): p is { obj: AnyObj; el: Element | undefined } => !!p.obj);
        // Shapes whose clip chain leaves nothing visible are dropped here, so
        // `livePairs` keeps objects and source elements aligned.
        const clippedAway = await flattenNestedClipPaths(pairs.map((p) => p.obj));
        const livePairs = pairs.filter((p) => !clippedAway.has(p.obj));
        const objs = livePairs.map((p) => p.obj);

        changeColors(objs);

        const grouped = groupObjectsBySVGHierarchy(
          objs,
          hiddenDiv.children[0] as unknown as Document,
          livePairs.map((p) => p.el)
        );

        // fabric 7 changed the default origin to center/center; this engine's
        // v1-inherited layout maths positions groups by their top-left edge,
        // so every group it places at (left, top) pins its origin explicitly.
        const img = new FabricGroup(grouped, {
          hasControls: true,
          selectable: true,
          evented: true,
          lockMovementX: false,
          flipY: true,
          ...TOP_LEFT_ORIGIN,
        }) as AnyObj;

        img.scaleToHeight(canvasResolution);
        img.set({ left: 0, top: 0 });
        img.setCoords();

        state.canvas.add(img);
        state.canvas.requestRenderAll();
        state.canvas.sendObjectToBack(img);

        state.design = img as FabricGroup;

        applyAnchorDifferences(state.design);

        if (input.safeZoneUrl) await addSafeZone();
        await addFixedLogos();
        await addLogos();
        await addTexts();
        await applyAllPatterns();

        if (input.sizesUrl && state.mainSizeId !== input.size) reScaleLogosAndTexts();
      } finally {
        hiddenDiv.parentNode?.removeChild(hiddenDiv);
      }
    }

    function changeColors(objects: AnyObj[]): void {
      if (!input.svgGroups?.length) return;
      objects.forEach((item) => {
        if (!item.id) return;
        const itemId = String(item.id).split('_')[0].toLowerCase();
        const group = input.svgGroups!.find((g) => g.id === itemId);
        if (!group) return;

        const fill = item.fill as any;
        if (fill?.gradientUnits) {
          const stops = fill.colorStops as { color: string }[];
          if (group.gradient_colors?.length) {
            group.gradient_colors.forEach((gc, i) => {
              if (stops[i]) stops[i].color = gc.color;
            });
            item.set('fill', new FabricGradient(fill));
          } else if (group.color && stops[0]) {
            stops[0].color = group.color;
            item.set('fill', new FabricGradient(fill));
          }
        } else {
          item.set(
            'fill',
            group.gradient_colors?.length ? group.gradient_colors[0].color : group.color
          );
        }
      });
    }

    /**
     * The element whose children are the design's top-level groups. Size-wrapped
     * masters nest the whole production atlas in ONE wrapper group (Production >
     * L > panels…, the reversible-master convention). Grouping by the highest
     * ancestor would fold every panel AND the Anchor into that wrapper: no anchor
     * alignment (fit-to-height fallback, artwork offset vs the builder), no
     * per-panel grading. Descend through single-<g> wrappers first.
     */
    function resolveContentRoot(svgDoc: Document): Element {
      const { root, wrappers } = descendSingleGroupWrappers(svgRootElement(svgDoc));
      // Only named wrappers are worth a note (a size group like "L"); parsers
      // add anonymous wrapper groups of their own.
      wrappers.filter(Boolean).forEach((id) => {
        const warning = `production-design-wrapper-unwrapped:${id}`;
        if (!state.warnings.includes(warning)) state.warnings.push(warning);
      });
      return root;
    }

    /**
     * The size the production atlas is drawn at: the masterfile's wrapper group
     * id when it has one (Production > L > …), else the platform default. This
     * is the grading base when the sizes SVG has no Anchor to say otherwise.
     */
    async function inspectDesignAtlas(): Promise<DesignAtlasInfo> {
      try {
        const svgText = sanitizedDesignText(await cachedText(storageUrl + input.designUrl));
        const doc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
        const { root, wrappers } = descendSingleGroupWrappers(doc.documentElement);
        const named = wrappers.filter((id) => !!id);
        return {
          baseToken: named.length ? named[named.length - 1] : PRODUCTION_BASE_SIZE,
          panelDims: measureDesignPanelDims(doc, root),
        };
      } catch {
        return { baseToken: PRODUCTION_BASE_SIZE, panelDims: {} };
      }
    }

    /**
     * Cut-panel dimensions of the atlas, keyed like the sizes spec. A panel
     * group's artwork is clipped by the panel shape, so the referenced clipPath
     * IS the panel; the group's own bbox (artwork bleed included) is the
     * fallback. DOM-only, nothing rendered.
     */
    function measureDesignPanelDims(doc: Document, contentRoot: Element): PanelSpec {
      const hiddenDiv = document.createElement('div');
      hiddenDiv.style.cssText = 'position:absolute;left:-9999px;visibility:hidden';
      hiddenDiv.appendChild(doc.documentElement);
      document.body.appendChild(hiddenDiv);
      const dims: PanelSpec = {};
      try {
        Array.from(contentRoot.children).forEach((g) => {
          if (g.tagName.toLowerCase() !== 'g' || !g.id || !g.id.toLowerCase().includes('panel'))
            return;
          const key = g.id.split('_')[0].toLowerCase();
          const ref = g
            .querySelector('[clip-path]')
            ?.getAttribute('clip-path')
            ?.match(/url\(["']?#([^"')]+)/)?.[1];
          let shape: Element | null = ref
            ? (hiddenDiv.querySelector(`[id="${cssEscape(ref)}"]`)?.firstElementChild ?? null)
            : null;
          const href = shape?.getAttribute('href') ?? shape?.getAttribute('xlink:href');
          if (shape?.tagName.toLowerCase() === 'use' && href?.startsWith('#')) {
            shape = hiddenDiv.querySelector(`[id="${cssEscape(href.slice(1))}"]`);
          }
          const bbox = ((shape ?? g) as SVGGraphicsElement).getBBox?.();
          if (bbox && bbox.width > 0 && bbox.height > 0) {
            dims[key] = { width: bbox.width, height: bbox.height };
          }
        });
      } finally {
        hiddenDiv.parentNode?.removeChild(hiddenDiv);
      }
      return dims;
    }

    /** Callers pass either a parsed Document or the mounted <svg> element itself. */
    function svgRootElement(svgDoc: Document): Element {
      return svgDoc.documentElement ?? (svgDoc as unknown as Element);
    }

    /**
     * Group fabric objects by their top-level `<g id>` in the source SVG.
     * `sourceElements[i]` (fabric's own parse of the same text) is preferred
     * over an id lookup in `svgDoc`, because normalized leaf ids repeat across
     * panels; the lookup is only the fallback for callers without elements.
     */
    function groupObjectsBySVGHierarchy(
      objects: AnyObj[],
      svgDoc: Document,
      sourceElements?: (Element | undefined)[]
    ): FabricObject[] {
      const groupMap = new Map<string, AnyObj[]>();
      // Roots per document: the walk must stop at the root of the document the
      // element actually lives in (fabric's parse vs the measuring mount).
      const rootsFor = (el: Element): { docRoot: Element; contentRoot: Element } => {
        const owner = el.ownerDocument?.documentElement;
        if (owner && owner !== svgRootElement(svgDoc)) {
          return { docRoot: owner, contentRoot: resolveContentRoot(owner.ownerDocument) };
        }
        return { docRoot: svgRootElement(svgDoc), contentRoot: resolveContentRoot(svgDoc) };
      };

      const looseItems: { obj: AnyObj; id: string }[] = [];
      // gids that come from a named <g>: always emitted as a group named after
      // it, even with one shape inside. The design editor writes FrontPanel /
      // BackPanel with a single Base polygon each; as bare leaves both were
      // "Base", the panel name collapsed and the back panel went missing.
      const namedGroups = new Set<string>();
      objects.forEach((obj, i) => {
        const el = sourceElements?.[i] ?? (obj.id ? svgDoc.getElementById(String(obj.id)) : null);
        if (!el) return;
        const { docRoot, contentRoot } = rootsFor(el);

        let parent = el.parentElement;
        let highestGroup: Element | null = null;
        while (parent && parent !== contentRoot && parent !== docRoot) {
          if (parent.tagName.toLowerCase() === 'g' && parent.id) highestGroup = parent;
          parent = parent.parentElement;
        }

        // Reached the document root without passing the atlas wrapper: the
        // element sits BESIDE the wrapper — artwork the customer drew straight
        // into the exported template (see descendSingleGroupWrappers). It is
        // not a panel; file it under the panel it lies on once those exist.
        if (parent === docRoot && contentRoot !== docRoot) {
          looseItems.push({ obj, id: String(highestGroup?.id ?? obj.id ?? `object-${i}`) });
          return;
        }

        const gid = (highestGroup?.id ?? String(obj.id ?? `object-${i}`)).toLowerCase();
        if (highestGroup) namedGroups.add(gid);
        if (!groupMap.has(gid)) groupMap.set(gid, []);
        groupMap.get(gid)!.push(obj);
      });

      assignLooseArtwork(groupMap, looseItems);

      const outs: { out: AnyObj; ratio?: { width: number; height: number } }[] = [];
      groupMap.forEach((items, gid) => {
        let out: AnyObj;
        if (items.length > 1 || namedGroups.has(gid)) {
          out = new FabricGroup(items) as AnyObj;
          out.id = gid;
        } else {
          out = items[0];
        }
        if (items.some((it) => it.productionLoose)) out.productionLoose = true;
        outs.push({ out, ratio: state.sizeDifferencesRatio[gid] });
      });

      // Grade every panel about its own centre, then SPREAD the layout about
      // the atlas centre (the Anchor's centre) by the LARGEST ratio of the
      // row: every centre-to-centre distance grows by m while no panel grows
      // by more than m, so panels that didn't overlap in the atlas can't
      // overlap on the graded sheet (a 4XL front and back used to overlap by
      // the whole width increase; per-panel spreading still left a collar
      // touching the sleeves). Ungraded groups (neck tape…) move with the
      // layout too. The sheet grows to fit — see fitCanvasToContent.
      const anchor = outs.find(
        (e) => typeof e.out.id === 'string' && e.out.id.toLowerCase().includes('anchor')
      )?.out;
      const atlasCenter = anchor?.getCenterPoint?.();
      const spread = Math.max(
        1,
        ...outs.flatMap((e) => (e.ratio ? [e.ratio.width, e.ratio.height] : []))
      );
      state.gradingSpread = atlasCenter ? spread : 1;
      outs.forEach(({ out, ratio }) => {
        if (out === anchor) return;
        const c0 = out.getCenterPoint();
        if (ratio) scaleKeepAnchor(out, ratio.width, ratio.height, 'center', 'center');
        if (atlasCenter && spread !== 1) {
          out.setPositionByOrigin(
            new Point(
              atlasCenter.x + (c0.x - atlasCenter.x) * spread,
              atlasCenter.y + (c0.y - atlasCenter.y) * spread
            ),
            'center',
            'center'
          );
          out.setCoords();
        }
      });

      return outs.map((e) => e.out);
    }

    /**
     * File artwork found outside the atlas wrapper under the panel it lies on:
     * the panel whose visible box contains the artwork's centre (the smallest
     * such box, so a strip inside a bigger panel's box keeps what sits on it),
     * else the panel it overlaps most. Grouped with the panel it grades,
     * isolates and PowerClips with it. Before this every such shape became a
     * "panel" of its own with a laser outline (a customer's back rectangle
     * shipped as a cut piece named "back") and the real panels collapsed into
     * one piece. Artwork on no panel keeps its own group, flagged so panel
     * extraction skips it, and is reported.
     */
    function assignLooseArtwork(
      groupMap: Map<string, AnyObj[]>,
      loose: { obj: AnyObj; id: string }[]
    ): void {
      if (!loose.length) return;
      const warn = (code: string) => {
        if (!state.warnings.includes(code)) state.warnings.push(code);
      };
      const panels = [...groupMap.entries()]
        .filter(([gid]) => !gid.includes('anchor'))
        .map(([gid, items]) => ({
          gid,
          box: items.reduce<CanvasBox | null>(
            (acc, it) => unionBox(acc, visibleCanvasBox(it)),
            null
          ),
        }))
        .filter((p): p is { gid: string; box: CanvasBox } => !!p.box);
      const area = (b: CanvasBox) => b.width * b.height;

      loose.forEach(({ obj, id }) => {
        const box = visibleCanvasBox(obj) ?? canvasBox(obj);
        const cx = box.left + box.width / 2;
        const cy = box.top + box.height / 2;
        const containing = panels.filter(
          (p) =>
            cx >= p.box.left &&
            cx <= p.box.left + p.box.width &&
            cy >= p.box.top &&
            cy <= p.box.top + p.box.height
        );
        let best: { gid: string; box: CanvasBox } | null = null;
        if (containing.length) {
          best = containing.reduce((a, b) => (area(a.box) <= area(b.box) ? a : b));
        } else {
          let bestArea = 0;
          for (const p of panels) {
            const overlap = intersectionArea(box, p.box);
            if (overlap > bestArea) {
              bestArea = overlap;
              best = p;
            }
          }
        }
        if (best) {
          groupMap.get(best.gid)!.push(obj);
          warn(`loose-artwork-assigned:${id}->${best.gid}`);
        } else {
          obj.productionLoose = true;
          const key = id.toLowerCase();
          if (!groupMap.has(key)) groupMap.set(key, []);
          groupMap.get(key)!.push(obj);
          warn(`loose-artwork-unassigned:${id}`);
        }
      });
    }

    function warnOnce(code: string): void {
      if (!state.warnings.includes(code)) state.warnings.push(code);
    }

    /**
     * The design SVG as fabric and the DOM should see it. The design editor
     * reuses one clipPath id per trimmed stripe; re-pointed to the definition
     * beside each user (svgSanitize). Same text for both parses, so fabric's
     * `elements[i]` still lines up with the measuring DOM.
     */
    function sanitizedDesignText(svgText: string): string {
      const { svg, renamed } = dedupeClipPathIds(svgText);
      if (renamed.length) warnOnce(`clippath-id-deduped:${renamed.length}`);
      return svg;
    }

    /**
     * fabric cannot export a clipPath whose own object carries a clipPath: the
     * inner clip never gets an id and toSVG() dies with "toString of
     * undefined". The design editor writes exactly that for a trimmed stripe —
     * rect(clip: trim) inside g(clip: sleeve) — which fabric's parser turns into
     * rect.clipPath = trim, trim.clipPath = sleeve. Replace the chain by ONE
     * clip: the geometric intersection, in the owner's clip space. An empty
     * intersection means the shape is clipped away entirely (the builder shows
     * nothing there either) — those objects are returned for removal. When the
     * geometry engine is unavailable the inner clip is dropped instead (the
     * shape may then bleed past its panel, but the file is written).
     */
    async function flattenNestedClipPaths(objects: AnyObj[]): Promise<Set<AnyObj>> {
      const clippedAway = new Set<AnyObj>();
      for (const obj of objects) {
        const name = String(obj.id ?? 'unnamed');
        let clip = obj.clipPath as AnyObj | undefined;
        for (let depth = 0; clip?.clipPath && depth < 4; depth++) {
          const merged = await intersectClipChain(clip);
          if (merged === 'empty') {
            clippedAway.add(obj);
            warnOnce(`nested-clippath-empty:${name}`);
            break;
          }
          if (merged) {
            obj.clipPath = merged;
            warnOnce(`nested-clippath-flattened:${name}`);
          } else {
            clip.clipPath = undefined;
            warnOnce(`nested-clippath-dropped:${name}`);
          }
          clip = obj.clipPath as AnyObj | undefined;
        }
        if (clippedAway.has(obj)) continue;
        // A group clip whose children carry clips can't be merged shape by shape.
        for (const child of (clip?._objects as AnyObj[] | undefined) ?? []) {
          if (child.clipPath) {
            child.clipPath = undefined;
            warnOnce(`nested-clippath-dropped:${name}`);
          }
        }
      }
      return clippedAway;
    }

    /**
     * Outline of a clip object as path data + the matrix placing it in the
     * clip owner's space — read back from fabric's own SVG of the object (its
     * own clip detached for the moment, or the export would hit the very bug
     * being worked around).
     */
    function clipOutlineSpec(clip: AnyObj): { d: string; matrix: Mat } | null {
      const inner = clip.clipPath;
      clip.clipPath = undefined;
      let markup = '';
      try {
        markup = clip.toSVG();
      } catch {
        return null;
      } finally {
        clip.clipPath = inner;
      }
      const doc = new DOMParser().parseFromString(
        `<svg xmlns="http://www.w3.org/2000/svg">${markup}</svg>`,
        'image/svg+xml'
      );
      const shape = doc.querySelector('path, polygon, polyline, rect, circle, ellipse');
      const d = shape ? shapeToPathData(shape) : null;
      if (!shape || !d) return null;
      const chain: Element[] = [];
      for (
        let el: Element | null = shape;
        el && el !== doc.documentElement;
        el = el.parentElement
      ) {
        chain.unshift(el);
      }
      let matrix: Mat = IDENTITY;
      chain.forEach((el) => {
        matrix = multiplyMat(matrix, parseTransform(el.getAttribute('transform')));
      });
      return { d, matrix };
    }

    async function intersectClipChain(clip: AnyObj): Promise<AnyObj | 'empty' | null> {
      const inner = clip.clipPath as AnyObj;
      if (clip.absolutePositioned || inner.absolutePositioned) return null;
      const outer = clipOutlineSpec(clip);
      const nested = clipOutlineSpec(inner);
      if (!outer || !nested) return null;
      // The inner clip's matrix is relative to the outer clip's centre.
      const d = await intersectPathData(
        [outer, { d: nested.d, matrix: multiplyMat(outer.matrix, nested.matrix) }],
        0.25
      );
      if (d === null) return null;
      if (d === '') return 'empty';
      // fabric positions a Path at its own bounds, so the geometry stays put.
      return new FabricPath(d) as AnyObj;
    }

    function scaleKeepAnchor(
      obj: AnyObj,
      newScaleX: number,
      newScaleY: number,
      anchorX: 'left' | 'center' | 'right' = (obj.originX as 'left' | 'center' | 'right') || 'left',
      anchorY: 'top' | 'center' | 'bottom' = (obj.originY as 'top' | 'center' | 'bottom') || 'top'
    ): void {
      if (obj.scaleX === newScaleX && obj.scaleY === newScaleY) return;
      const anchor = obj.getPointByOrigin(anchorX, anchorY);
      obj.set({ scaleX: newScaleX, scaleY: newScaleY });
      obj.setPositionByOrigin(anchor, anchorX, anchorY);
      obj.setCoords();
    }

    function findMostLeftAndTopElements(d: FabricGroup): { maxLeft: number; maxTop: number } {
      const objs = (d as AnyObj)._objects as AnyObj[] | undefined;
      if (!objs) return { maxLeft: 0, maxTop: 0 };

      let maxLeft = Infinity,
        maxTop = Infinity;
      objs.forEach((o) => {
        const { x, y } = topLeftOf(o);
        if (x < maxLeft) maxLeft = x;
        if (y < maxTop) maxTop = y;
      });
      return { maxLeft: isFinite(maxLeft) ? maxLeft : 0, maxTop: isFinite(maxTop) ? maxTop : 0 };
    }

    /**
     * Top-left corner of an object in its parent's space, whatever its origin.
     * Under fabric 6 `left/top` WERE the top-left edge; under fabric 7 they
     * are the centre by default, which silently shifted the anchor alignment
     * by half a panel.
     */
    function topLeftOf(o: AnyObj): { x: number; y: number } {
      if (typeof o.getPointByOrigin === 'function') {
        const p = o.getPointByOrigin('left', 'top');
        return { x: p.x, y: p.y };
      }
      return { x: o.left ?? 0, y: o.top ?? 0 };
    }

    function applyAnchorDifferences(d: FabricGroup): void {
      const allObjs = (d as AnyObj)._objects as AnyObj[] | undefined;
      if (!allObjs) return;

      const anchors = allObjs.filter(
        (o) => typeof o.id === 'string' && o.id.toLowerCase().includes('anchor')
      );
      if (!anchors.length) return;

      const anchorGroup = new FabricGroup(cloneFabricObjects(anchors), {
        hasControls: false,
        selectable: false,
        evented: false,
        lockMovementX: true,
        lockMovementY: true,
        flipY: true,
        ...TOP_LEFT_ORIGIN,
      });
      anchorGroup.scaleToHeight(canvasResolution);
      anchorGroup.set({ left: 0, top: 0 }).setCoords();

      (d as AnyObj).set({ scaleX: anchorGroup.scaleX, scaleY: anchorGroup.scaleY }).setCoords();

      const { maxLeft, maxTop } = findMostLeftAndTopElements(d);
      const { x: al, y: at } = anchors[0] ? topLeftOf(anchors[0]) : { x: 0, y: 0 };
      const sx = anchorGroup.scaleX ?? 1;
      const sy = anchorGroup.scaleY ?? 1;

      const diffX =
        maxLeft < 0 && al < 0 ? Math.abs(maxLeft - al) * sx : Math.abs(al - maxLeft) * sx;
      const diffY = maxTop < 0 && at < 0 ? Math.abs(maxTop - at) * sy : Math.abs(at - maxTop) * sy;

      const heightDiff = d.getScaledHeight() - canvasResolution;
      (d as AnyObj)
        .set({
          left: ((d as AnyObj).left ?? 0) - diffX,
          top: ((d as AnyObj).top ?? 0) - (heightDiff - diffY),
        })
        .setCoords();
    }

    async function addSafeZone(): Promise<void> {
      const svgText = await cachedText(storageUrl + input.safeZoneUrl);
      const { objects } = await loadSVGFromString(svgText);
      const clip = new FabricGroup((objects ?? []) as FabricObject[], {
        hasControls: false,
        selectable: false,
        evented: false,
        lockMovementX: true,
        lockMovementY: true,
        absolutePositioned: true,
        inverted: true,
        flipY: true,
        ...TOP_LEFT_ORIGIN,
      }) as AnyObj;

      state.canvas.centerObject(clip);
      clip.scaleToHeight(canvasResolution);
      clip.set({ left: 0, top: 0 }).setCoords();
      state.safeZone = clip as FabricGroup;
    }

    async function addFixedLogos(): Promise<void> {
      for (const logo of input.logos ?? []) await addSvgLogo(logo);
    }

    async function addSvgLogo(logo: LogoConfig): Promise<void> {
      const url = encodeURI((storageUrl + logo.url).trim()) + '?nocache=11';
      const { objects } = await loadSVGFromURL(url);
      const img = new FabricGroup((objects ?? []) as FabricObject[]) as AnyObj;

      img.set({
        left: logo.left,
        top: logo.top,
        scaleX: logo.scaleX ?? 1,
        scaleY: logo.scaleY ?? 1,
        angle: logo.angle ?? 0,
        hasControls: false,
        selectable: false,
        evented: false,
        lockMovementX: true,
        lockMovementY: true,
        globalCompositeOperation: 'source-atop',
        originX: 'center',
        originY: 'center',
        flipX: true,
        type: 'logo',
      });

      if (logo.is_customizable) {
        const svgGroup = input.svgGroups?.find((g) => g.id === (logo.placement_title ?? ''));
        if (svgGroup) {
          const logoObjs: AnyObj[] = (img as AnyObj)._objects ?? [img];
          logoObjs.forEach((o) => o.set({ fill: svgGroup.color }));
        }
      }

      state.canvas.add(img);
      state.canvas.requestRenderAll();
    }

    async function addLogos(): Promise<void> {
      for (const logo of input.custom_logos ?? []) {
        if (logo?.url) await addLogo(logo);
      }
    }

    async function addLogo(logo: CustomLogoConfig): Promise<void> {
      const url = encodeURI((storageUrl + logo.url).trim()) + '?nocache=11';
      const base64 = await cachedDataUrl(url);

      const img = (await FabricImage.fromURL(base64, { crossOrigin: 'anonymous' })) as AnyObj;

      const ar = ((img.width ?? 1) as number) / ((img.height ?? 1) as number);
      if (ar > 1) img.scaleToWidth(logo.height * canvasWidthRatio);
      else img.scaleToHeight(logo.height * canvasHeightRatio);

      const rot =
        logo.rotation < 0 ? oppositeAngle(360 - logo.rotation) : oppositeAngle(logo.rotation);

      img.set({
        left: logo.x_axis_3d ?? logo.x_axis,
        top: logo.y_axis_3d ?? logo.y_axis,
        angle: rot,
        hasControls: false,
        selectable: false,
        evented: false,
        lockMovementX: true,
        lockMovementY: true,
        globalCompositeOperation: 'source-atop',
        originX: 'center',
        originY: 'center',
        flipX: true,
        type: 'logo',
      });

      if (logo.scaleX && logo.scaleY) {
        img.scaleX = logo.scaleX * canvasWidthRatio;
        img.scaleY = logo.scaleY * canvasHeightRatio;
      }

      img.clipPath = state.safeZone;
      state.canvas.add(img);
      state.canvas.requestRenderAll();
      state.customLogoKeys.push(logo.url);
    }

    async function addTexts(): Promise<void> {
      for (const t of input.texts ?? []) if (t?.value) await addText(t);
    }

    async function addText(customText: CustomTextConfig): Promise<void> {
      const font = await cachedFont(storageUrl + customText.font_url);
      if (!font) return;

      const path = font.getPath(customText.value, 0, 0, 72, {
        features: { liga: true, rlig: true },
      });

      const wrapSvg = (body: string) =>
        '<?xml version="1.0" encoding="utf-8"?>\n' +
        '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"' +
        ' version="1.1" xml:space="preserve">\n' +
        body +
        '\n</svg>';
      const straightSvg = wrapSvg(path.toSVG());

      // A product may define several placements for one text (number on the
      // back AND the front); the customer switches them off in the customizer
      // (`selected: false`) and the builder skips them — so must the file.
      const placed = customText.items.filter((item) => isTextPlacementOn(item));

      await Promise.all(
        placed.map(async (item) => {
          const strokeWidth = parseInt(String(item.outline_width), 10) || 0;
          const outlined = strokeWidth > 0 && !!item.outline_color;

          // Arched placements: the builder persists only the arc parameters
          // and re-derives the curve on every render — so must this file, or
          // an arched name prints straight (see utils/productionFile/arcText).
          const arcMode = wantsArcText(item) ? effectiveArcMode(item.arc_mode) : null;
          const arc = arcMode
            ? layoutArcText(font, customText.value, {
                mode: arcMode,
                strength: Number(item.arc_strength ?? 25),
                flipped: !!item.arc_flipped,
                variant: (item.arc_variant ?? 'loft') as ArcVariant,
                strokeWidth: outlined ? strokeWidth : 0,
              })
            : null;
          const textSvg = arc ? wrapSvg(arc.paths.join('')) : straightSvg;
          const paintStrokeWidth = arc ? arc.renderStrokeWidth : strokeWidth;

          const { objects } = await loadSVGFromString(textSvg);
          const glyphPaths = (objects ?? []) as AnyObj[];

          // Paint the glyph paths themselves (fabric v6 groups don't paint; the
          // shipped port styled the Group and every name/number came out black).
          //
          // Outline = a stroke-only copy of the glyphs UNDER a fill-only copy.
          // The builder paints with paintFirst:'stroke' (the inner half of the
          // stroke hidden under the fill, so the visible outline is
          // strokeWidth/2 outward). fabric serialises that as
          // paint-order="stroke", which CorelDRAW's SVG import ignores — it
          // painted the stroke centred over the fill, eating into the letters.
          // Two stacked copies give the same outward-only look everywhere.
          glyphPaths.forEach((p) => p.set({ fill: item.color, stroke: null, strokeWidth: 0 }));
          let parts: AnyObj[] = glyphPaths;
          if (outlined) {
            const { objects: outlineObjs } = await loadSVGFromString(textSvg);
            const outlinePaths = (outlineObjs ?? []) as AnyObj[];
            // Round joins: with the default miter join (limit 4) the acute
            // corners of condensed sports fonts (the M's inner V, N/K
            // diagonals) grow long miter spikes past the glyph — visible as
            // black needles on the print. Round joins hug the letter shape.
            outlinePaths.forEach((p) =>
              p.set({
                fill: item.outline_color,
                stroke: item.outline_color,
                strokeWidth: paintStrokeWidth,
                strokeLineJoin: 'round',
                strokeLineCap: 'round',
              })
            );
            parts = [...outlinePaths, ...glyphPaths];
          }

          const fabricText = new FabricGroup(parts as FabricObject[]) as AnyObj;
          if (arc) {
            // The builder sizes an arc by the STRAIGHT text's natural scale for
            // this height, compensated by 72/renderSize (arcTextSizing.ts).
            const { objects: refObjs } = await loadSVGFromString(straightSvg);
            const ref = new FabricGroup((refObjs ?? []) as FabricObject[]) as AnyObj;
            ref.scaleToHeight(item.height);
            const comp = arcSizeCompensation(arcMode);
            fabricText.set({ scaleX: (ref.scaleX ?? 1) * comp, scaleY: (ref.scaleY ?? 1) * comp });
          } else {
            fabricText.scaleToHeight(item.height);
          }

          const rot =
            item.rotation < 0 ? oppositeAngle(360 - item.rotation) : oppositeAngle(item.rotation);

          fabricText.set({
            left: item.x_axis_3d ?? item.x_axis,
            top: item.y_axis_3d ?? item.y_axis,
            angle: rot,
            hasControls: false,
            selectable: false,
            evented: false,
            lockMovementX: true,
            lockMovementY: true,
            globalCompositeOperation: 'source-atop',
            lockScalingFlip: true,
            originX: 'center',
            originY: 'center',
            flipX: true,
            type: 'text',
          });

          if (item.scaleX && item.scaleY) {
            // The builder persists CANONICAL scale and paints the 3D texture at
            // canonical × ARTWORK_SIZING_RATIO (760/600) — the same factor its
            // size badge uses — so the texture, the reported cm and this file
            // agree only when the same ratio is applied here (see
            // COMPOSE_DEFAULTS). The v1-era 512/600 printed ~0.85× the builder;
            // applying canonical 1:1 printed ~0.79× of it.
            //
            // Arcs: a design saved under the canonical-scale logic (AB#16165,
            // `arc_scale_canonical`) stores the straight-bbox scale, which the
            // builder compensates by 72/renderSize; a legacy arc design stores
            // the applied scale and is used as is — the same two branches as
            // the builder's useTexts.ts.
            const comp =
              arc && item.arc_scale_canonical === true ? arcSizeCompensation(arcMode) : 1;
            fabricText.scaleX = item.scaleX * canvasWidthRatio * comp;
            fabricText.scaleY = item.scaleY * canvasHeightRatio * comp;
          }

          if (arc && (arc.centerOffset.x || arc.centerOffset.y)) {
            // The builder's warped-arc object is the warped RASTER canvas,
            // centred on the canvas (padding and arch headroom included); this
            // group is centred on the glyphs. Shift the group so the glyphs
            // land where the customer saw them.
            fabricText.setCoords();
            const centre = fabricText.getCenterPoint();
            const rasterCentre = fabricUtil.transformPoint(
              new Point(arc.centerOffset.x, arc.centerOffset.y),
              fabricText.calcTransformMatrix()
            );
            fabricText.setPositionByOrigin(
              new Point(2 * centre.x - rasterCentre.x, 2 * centre.y - rasterCentre.y),
              'center',
              'center'
            );
          }

          fabricText.clipPath = state.safeZone;
          state.canvas.add(fabricText);
          state.canvas.requestRenderAll();
        })
      );
    }

    async function applyAllPatterns(): Promise<void> {
      await Promise.all(Object.keys(groupPatterns).map((part) => applyPattern(part)));
    }

    async function applyPattern(svgPart: string): Promise<void> {
      const patternData = groupPatterns[svgPart];
      if (!patternData?.name || !patternData?.url) return;

      let pattern =
        state.patterns[svgPart] instanceof FabricPattern
          ? (state.patterns[svgPart] as FabricPattern)
          : undefined;

      if (!pattern) {
        const created = await setupPattern(svgPart, patternData, state.design);
        if (!created) return;
        pattern = created;
        state.patterns[svgPart] = pattern;
      }

      const designObjs = ((state.design as AnyObj)._objects as AnyObj[]) ?? [
        state.design as AnyObj,
      ];

      designObjs.flat().forEach((dObj: AnyObj, index: number) => {
        dObj.set('id', String(dObj.id).split('_')[0]);
        if (String(dObj.id).toLowerCase() !== svgPart) return;

        const latestObjs = (state.design as AnyObj)._objects as AnyObj[];

        const clipGroup = new FabricGroup(cloneFabricObjects(latestObjs), {
          hasControls: false,
          selectable: false,
          evented: false,
          lockMovementX: true,
          lockMovementY: true,
          absolutePositioned: true,
          flipY: true,
          ...TOP_LEFT_ORIGIN,
        }) as AnyObj;

        const patternId = `pattern_group_${svgPart}`;
        const occCount = latestObjs.filter((o) => String(o.id) === patternId).length;
        const objNum = index + occCount;

        const clipObjs = (clipGroup as AnyObj)._objects as AnyObj[];
        clipObjs
          .filter((_: AnyObj, i: number) => i !== objNum)
          .forEach((o: AnyObj) => (clipGroup as FabricGroup).remove(o));

        const clipTarget = ((clipGroup as AnyObj)._objects as AnyObj[])[0];
        clipTarget.clipPath = undefined;
        clipTarget.originalMatrix = clipTarget.calcTransformMatrix(true);

        clipGroup.scaleToHeight(canvasResolution);
        clipGroup.set({ left: 0, top: 0 }).setCoords();

        const finalPattern =
          occCount > 0
            ? (shallowClone(pattern! as unknown as object) as unknown as FabricPattern)
            : pattern!;

        (finalPattern as unknown as AnyObj).id = `pattern_${svgPart}_${occCount}`;

        const patternRect = new FabricRect({
          left: dObj.left,
          top: dObj.top,
          width: dObj.width,
          height: dObj.height,
          hasControls: false,
          selectable: false,
          evented: false,
          originX: dObj.originX || 'left',
          originY: dObj.originY || 'top',
          lockMovementX: true,
          lockMovementY: true,
          absolutePositioned: true,
          fill: finalPattern as any,
        }) as AnyObj;
        patternRect.clipPath = clipGroup;
        patternRect.id = patternId;

        const withRect = cloneFabricObjects(latestObjs);
        withRect.splice(objNum + 1, 0, patternRect);
        regroupDesign(withRect);

        state.patterns[svgPart] = patternRect as FabricRect;
      });

      state.canvas.requestRenderAll();
    }

    async function setupPattern(
      svgPart: string,
      patternData: PatternConfig,
      d: FabricGroup,
      BASE_DESIGN_WIDTH = 6000
    ): Promise<FabricPattern | null> {
      const { objects } = await loadSVGFromURL(
        `${storageUrl}${patternData.pattern_url}?q=${patternData.random_string}`
      );
      if (!objects?.length) return null;

      const img = new FabricGroup((objects ?? []) as FabricObject[]) as AnyObj;
      const partCfg = groupPatterns[svgPart];

      if (partCfg.color?.value) {
        const fill = partCfg.color.value;
        if ((img as AnyObj)._objects) {
          img.getObjects().forEach((o) => (o as AnyObj).set('fill', fill));
        } else {
          img.set('fill', fill);
        }
      }

      const scaleVal = parseInt(String(partCfg.scale), 10);
      img.scale((((d as AnyObj).width as number) / BASE_DESIGN_WIDTH) * (scaleVal / 100));

      const sw = img.getScaledWidth();
      const sh = img.getScaledHeight();

      const svgEl = new DOMParser().parseFromString(
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${sw} ${sh}" width="${sw}" height="${sh}">${img.toSVG()}</svg>`,
        'image/svg+xml'
      ).documentElement;

      const svgImage = Object.assign(new Image(), {
        crossOrigin: 'Anonymous',
        src: svgToDataURL(svgEl),
      });
      const pat = new FabricPattern({ source: svgImage, repeat: 'repeat' });

      const angle = partCfg?.angle ?? 0;
      const rad = (angle * Math.PI) / 180;
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);
      const tx = (sw / 2) * (1 - cos) + (sh / 2) * sin;
      const ty = (sh / 2) * (1 - cos) - (sw / 2) * sin;

      pat.patternTransform = [cos, sin, -sin, cos, -tx, -ty] as any;

      return pat;
    }

    function reScaleLogosAndTexts(): void {
      // v1 (fabric v5) matched on the 'logo'/'text' type tags; v6 drops them
      // (readonly getter), which silently turned this into a no-op in the
      // shipped port. Same structural identification as extractOverlays().
      //
      // Each overlay grades by the panel it SITS ON — the design child with
      // the largest intersection area (v1 took the FIRST bbox overlap, which
      // routinely matched a neighboring panel: atlas bboxes overlap heavily,
      // so a back number could grade by the sleeve's ratios). Production
      // norm: uniform scaling by the panel's HEIGHT difference only (v1
      // additionally mixed in the width delta whenever it changed less).
      const gradedPanels = (state.design.getObjects() as AnyObj[])
        .map((dObj) => ({
          ratio: state.sizeDifferencesRatio[String(dObj.id)],
          box: canvasBox(dObj),
        }))
        .filter((entry) => !!entry.ratio);

      overlayObjects().forEach((lt: AnyObj) => {
        const ltBox = canvasBox(lt);
        let best: {
          ratio: { width: number; height: number };
          box: CanvasBox;
          area: number;
        } | null = null;
        for (const entry of gradedPanels) {
          const area = intersectionArea(ltBox, entry.box);
          if (area > 0 && (!best || area > best.area)) {
            best = { ratio: entry.ratio!, box: entry.box, area };
          }
        }
        if (!best) return;
        const factor = best.ratio.height || 1;
        lt.scaleX = (lt.scaleX ?? 1) * factor;
        lt.scaleY = (lt.scaleY ?? 1) * factor;

        // The panel was graded about its ORIGINAL centre c0 and the layout
        // then spread about the atlas centre C by m (groupObjectsBySVGHierarchy),
        // so its graded centre is c1 = C + (c0 − C)·m and every fabric point
        // p maps to c1 + (p − c0)·ratio. A placement the customer made must
        // follow the same map or a 4XL back number sits visibly off the spot
        // it had on the L panel in the builder. `left/top` ARE the overlay's
        // centre (originX/Y center on every text and logo); best.box is the
        // graded panel box, so its centre is c1.
        const c1x = best.box.left + best.box.width / 2;
        const c1y = best.box.top + best.box.height / 2;
        const anchorObj = (state.design.getObjects() as AnyObj[]).find(
          (o) => typeof o.id === 'string' && o.id.toLowerCase().includes('anchor')
        );
        const m = anchorObj ? state.gradingSpread || 1 : 1;
        let c0x = c1x;
        let c0y = c1y;
        if (anchorObj && m !== 1) {
          const pivot = canvasBox(anchorObj);
          const Cx = pivot.left + pivot.width / 2;
          const Cy = pivot.top + pivot.height / 2;
          c0x = Cx + (c1x - Cx) / m;
          c0y = Cy + (c1y - Cy) / m;
        }
        lt.set({
          left: c1x + ((lt.left ?? c0x) - c0x) * (best.ratio.width || 1),
          top: c1y + ((lt.top ?? c0y) - c0y) * (best.ratio.height || 1),
        });
        lt.setCoords();
      });
      state.canvas.requestRenderAll();
    }

    function regroupDesign(designObjects: FabricObject[]): void {
      const g = new FabricGroup(designObjects, {
        hasControls: false,
        selectable: false,
        evented: false,
        lockMovementX: true,
        lockMovementY: true,
        flipY: true,
        ...TOP_LEFT_ORIGIN,
      });
      g.scaleToHeight(canvasResolution);
      g.set({ left: 0, top: 0 }).setCoords();

      state.canvas.remove(state.design);
      state.canvas.add(g);
      state.canvas.sendObjectToBack(g);

      applyAnchorDifferences(g);
      state.design = g;
    }

    function resolveTargetDimensions(): { widthPt: number; heightPt: number } {
      let targetWidth: number = (state.design as AnyObj)?.width ?? canvasResolution;
      let targetHeight: number = (state.design as AnyObj)?.height ?? canvasResolution;

      // Depth-first: the Anchor normally sits at the top level, but a master
      // whose wrapper could not be unwrapped may still carry it one level down.
      const findAnchor = (objs: AnyObj[]): AnyObj | undefined => {
        for (const o of objs) {
          if (typeof o.id === 'string' && o.id.toLowerCase().includes('anchor')) return o;
          const nested = o._objects as AnyObj[] | undefined;
          const hit = nested ? findAnchor(nested) : undefined;
          if (hit) return hit;
        }
        return undefined;
      };
      const anchor = findAnchor((state.design?.getObjects() ?? []) as AnyObj[]);

      if (anchor?.width && anchor?.height) {
        targetWidth = anchor.width as number;
        targetHeight = anchor.height as number;
      }
      return { widthPt: targetWidth, heightPt: targetHeight };
    }

    /**
     * Locate cut-panel bounds. Panels live as clipPaths in the production
     * SVG's <defs> with ids containing "Panel" (FrontPanel, LeftSleevePanel…);
     * fabric attaches them as `.clipPath` on the parsed children. Top-level
     * groups are COLOR zones (can merge both sleeves) — only a last-resort
     * fallback, flagged with a warning.
     */
    function extractPanels(): PanelBox[] {
      const found: PanelBox[] = [];
      const seen = new Set<string>();

      const record = (
        name: string,
        box: { left: number; top: number; width: number; height: number },
        source: PanelBoxSource,
        sourceId?: string
      ) => {
        const key = name.toLowerCase();
        if (!key || seen.has(key)) return;
        seen.add(key);
        found.push({ name, ...box, source, sourceId });
      };

      // The design editor names its stripe TRIM clips "<stripe>__panel" /
      // "__trim" (a clip that trims a stripe to a panel, not a panel). In a
      // re-saved design those are the only clipPath ids containing "panel",
      // so taking them as panels yielded a single "stripe 3" panel (26529).
      const isTrimClipId = (id: string) => /__(panel|trim)(-d+)?$/i.test(id);
      const namesPanel = (id: unknown): id is string =>
        typeof id === 'string' && id.toLowerCase().includes('panel') && !isTrimClipId(id);

      const panelIdOf = (obj: AnyObj): string | null => {
        const own = namesPanel(obj.id) ? obj.id : null;
        if (own) return own;
        const children = (obj._objects as AnyObj[]) ?? [];
        for (const child of children) {
          const hit = panelIdOf(child);
          if (hit) return hit;
        }
        return null;
      };

      const matrixBox = (w: number, h: number, matrix: number[]) => {
        const corners = [
          { x: -w / 2, y: -h / 2 },
          { x: w / 2, y: -h / 2 },
          { x: w / 2, y: h / 2 },
          { x: -w / 2, y: h / 2 },
        ].map((p) => fabricUtil.transformPoint(p as any, matrix as never));
        const xs = corners.map((p) => p.x);
        const ys = corners.map((p) => p.y);
        const left = Math.min(...xs);
        const top = Math.min(...ys);
        return { left, top, width: Math.max(...xs) - left, height: Math.max(...ys) - top };
      };

      const clipBoxOnCanvas = (owner: AnyObj, clip: AnyObj) => {
        const clipMatrix = clip.calcTransformMatrix();
        const matrix = clip.absolutePositioned
          ? clipMatrix
          : fabricUtil.multiplyTransformMatrices(owner.calcTransformMatrix(), clipMatrix);
        return matrixBox((clip.width ?? 0) as number, (clip.height ?? 0) as number, matrix);
      };

      // Canvas-space box of a (possibly nested) object. getBoundingRect() is
      // NOT safe here: for children inside a group it goes stale once the
      // group is rescaled afterwards (the anchor pass does exactly that on
      // graded rows). calcTransformMatrix() composes the live group chain.
      const objectBoxOnCanvas = (obj: AnyObj) =>
        matrixBox(
          (obj.width ?? 0) as number,
          (obj.height ?? 0) as number,
          obj.calcTransformMatrix()
        );

      const walk = (objs: AnyObj[]) => {
        for (const obj of objs) {
          const clip = obj.clipPath as AnyObj | undefined;
          if (clip) {
            const panelId = panelIdOf(clip);
            if (panelId) record(panelId.split('_')[0], clipBoxOnCanvas(obj, clip), 'clipPath');
          }
          const children = (obj._objects as AnyObj[]) ?? [];
          if (children.length) walk(children);
        }
      };

      const topLevel = (state.design?.getObjects() ?? []) as AnyObj[];
      walk(topLevel);

      if (!found.length) {
        // Strategy 2: leaves whose own id names a panel.
        const walkLeaves = (objs: AnyObj[]) => {
          for (const obj of objs) {
            const children = (obj._objects as AnyObj[]) ?? [];
            if (children.length) {
              walkLeaves(children);
            } else if (namesPanel(obj.id)) {
              record(obj.id.split('_')[0], objectBoxOnCanvas(obj), 'leafId', obj.id);
            }
          }
        };
        walkLeaves(topLevel);
      }

      if (!found.length && topLevel.length) {
        for (const obj of topLevel) {
          if (typeof obj.id !== 'string' || !obj.id) continue;
          if (obj.id.toLowerCase().includes('anchor')) continue;
          // Stray artwork outside every panel is not a panel (assignLooseArtwork).
          if (obj.productionLoose) continue;
          // Clip-aware: the group's plain bbox includes clipped background
          // shapes and would make every panel window swallow its neighbours.
          record(
            obj.id.split('_')[0],
            visibleCanvasBox(obj) ?? objectBoxOnCanvas(obj),
            'topGroup',
            obj.id
          );
        }
        // Many masterfiles carry the panels directly as top-level groups
        // (FrontPanel, BackPanel, …) — that's fine. Only warn when the groups
        // don't look like panels at all (color zones like base/Sleeves).
        if (!found.some((p) => /panel/i.test(p.name))) {
          state.warnings.push('panel-namespace-missing');
        }
      }

      return found;
    }

    /**
     * Grow the (square) canvas so every visible panel and overlay fits, keeping
     * the physical scale per unit. Content is shifted right/down when it
     * starts before the origin. Returns the new square side (>= the base
     * resolution; unchanged for base-size rows whose atlas fits its Anchor).
     */
    function fitCanvasToContent(): number {
      const designKids = (state.design?.getObjects() ?? []) as AnyObj[];
      const boxes: CanvasBox[] = [];
      designKids.forEach((o) => {
        const isAnchor = typeof o.id === 'string' && o.id.toLowerCase().includes('anchor');
        const box = isAnchor ? canvasBox(o) : visibleCanvasBox(o);
        if (box) boxes.push(box);
      });
      overlayObjects().forEach((o) => boxes.push(canvasBox(o)));
      if (!boxes.length) return canvasResolution;

      const minX = Math.min(...boxes.map((b) => b.left));
      const minY = Math.min(...boxes.map((b) => b.top));
      const maxX = Math.max(...boxes.map((b) => b.left + b.width));
      const maxY = Math.max(...boxes.map((b) => b.top + b.height));
      const dx = Math.max(0, -minX);
      const dy = Math.max(0, -minY);
      if (dx || dy) {
        const movable: AnyObj[] = [state.design as AnyObj, ...overlayObjects()];
        if (state.safeZone) movable.push(state.safeZone as AnyObj);
        movable.forEach((o) => {
          o.set({ left: (o.left ?? 0) + dx, top: (o.top ?? 0) + dy });
          o.setCoords();
        });
      }
      const size = Math.ceil(Math.max(canvasResolution, maxX + dx, maxY + dy));
      if (size !== canvasResolution) state.canvas.setDimensions({ width: size, height: size });
      return size;
    }

    function generateProductionSVG(
      targetWidth: number,
      targetHeight: number,
      outputSize: number = canvasResolution
    ): string {
      const matrixMap = new Map<string, string>();

      state.design
        .getObjects()
        .flat()
        .forEach((dObj: AnyObj) => {
          if (String(dObj.id).includes('pattern_') && dObj.clipPath) {
            const clipObj = ((dObj.clipPath as AnyObj)._objects as AnyObj[])[0];
            const cur = (clipObj.calcTransformMatrix() as number[]).map(roundOff).join(' ');
            const orig = (clipObj.originalMatrix as number[]).map(roundOff).join(' ');
            matrixMap.set(cur, orig);
          }
        });

      fabricConfig.NUM_FRACTION_DIGITS = 6;

      let svg = state.canvas.toSVG();
      svg = svg.replace(/^\s*(<\?xml[^>]*>\s*)?(<!DOCTYPE[^>]*>\s*)?/i, '');

      svg = /width="[^"]+"/i.test(svg)
        ? svg.replace(/(width=")([^"]+)(")/i, `$1${targetWidth}$3`)
        : svg.replace(/(<svg)(\s)/i, `$1 width="${targetWidth}"$2`);

      svg = /height="[^"]+"/i.test(svg)
        ? svg.replace(/(height=")([^"]+)(")/i, `$1${targetHeight}$3`)
        : svg.replace(/(<svg)(\s)/i, `$1 height="${targetHeight}"$2`);

      matrixMap.forEach((original, current) => {
        const safe = current.replace(/\./g, '\\.').replace(/-/g, '\\-');
        svg = svg.replace(new RegExp(`matrix\\(${safe}\\)`, 'g'), `matrix(${original})`);
      });

      svg = svg
        .replace(/(<svg[^>]*>)/i, `$1<g transform="scale(1,-1) translate(0, -${outputSize})">`)
        .replace(/<\/svg>/i, '</g></svg>');

      Object.keys(state.patterns).forEach((svgPart) => {
        const angle = groupPatterns[svgPart]?.angle;
        if (!angle) return;
        const rx = new RegExp(`<pattern([^>]+id="SVGID_pattern_${svgPart}_\\d+"[^>]*)>`, 'g');
        svg = svg.replace(rx, (_m, attrs: string) =>
          /patternTransform\s*=/.test(attrs)
            ? `<pattern ${attrs.replace(/patternTransform="[^"]*"/, `patternTransform="rotate(${angle})"`)}>`
            : `<pattern ${attrs} patternTransform="rotate(${angle})">`
        );
      });

      return svg;
    }
  }

  return { compose, cache };
}

function cssEscape(value: string): string {
  const cssApi = (globalThis as any).CSS;
  if (cssApi?.escape) return cssApi.escape(value);
  return value.replace(/["\\]/g, '\\$&');
}
